import React, { useState } from 'react';
import { fetchDeletionRequests, adminDeleteUser, adminGrantAccess, searchUserAttempts } from '../api';
import { toast } from 'react-toastify';
import Particle from '../componets/Particle';
import option2 from "../assets/json/option2.json";
import './AdminPage.css';



function AdminPage() {
    const [password, setPassword] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [requests, setRequests] = useState([]);
    const [manualEmail, setManualEmail] = useState('');
    const [grantEmail, setGrantEmail] = useState('');
    const [grantExamType, setGrantExamType] = useState('celpip');
    const [grantDays, setGrantDays] = useState(30);
    const [isLoading, setIsLoading] = useState(false);
    const [savedPassword, setSavedPassword] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchTestType, setSearchTestType] = useState('');
    const [searchSection, setSearchSection] = useState('');
    const [searchDateRange, setSearchDateRange] = useState('all_time');
    const [searchStartDate, setSearchStartDate] = useState('');
    const [searchEndDate, setSearchEndDate] = useState('');
    const [attempts, setAttempts] = useState([]);
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const [isSearching, setIsSearching] = useState(false);



    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        if (!password) {
            toast.error("Please enter the administrator password.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetchDeletionRequests(password);
            if (response.data && response.data.success) {
                setIsAuthenticated(true);
                setSavedPassword(password);
                setRequests(response.data.requests || []);
                toast.success("Authenticated successfully!");
                // Trigger initial search for attempts
                handleSearchSubmit(null, password);
            } else {
                toast.error("Authentication failed. Please check the password.");
            }
        } catch (error) {
            console.error("Admin authentication error:", error);
            toast.error(
                error.response?.data?.message || 
                "Authentication failed. Invalid password."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const loadRequests = async () => {
        try {
            const response = await fetchDeletionRequests(savedPassword);
            if (response.data && response.data.success) {
                setRequests(response.data.requests || []);
            }
        } catch (error) {
            toast.error("Error reloading deletion requests.");
        }
    };

    const handleDeleteClick = async (email) => {
        const confirmDelete = window.confirm(
            `WARNING: Are you sure you want to permanently delete the user account and ALL associated test data for ${email}?\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) return;

        setIsLoading(true);
        try {
            const response = await adminDeleteUser(email, savedPassword);
            if (response.data && response.data.success) {
                toast.success(response.data.message || "User data deleted successfully.");
                loadRequests();
            } else {
                toast.error(response.data.message || "Failed to delete user.");
            }
        } catch (error) {
            console.error("Delete user error:", error);
            toast.error(
                error.response?.data?.message || 
                "Error deleting user."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleManualDeleteSubmit = async (e) => {
        e.preventDefault();
        if (!manualEmail) {
            toast.error("Please enter an email address.");
            return;
        }

        const confirmDelete = window.confirm(
            `MANUAL ACTION: Are you sure you want to permanently delete the user ${manualEmail} and all of their practice attempts?\n\nThis is an administrative delete override.`
        );

        if (!confirmDelete) return;

        setIsLoading(true);
        try {
            const response = await adminDeleteUser(manualEmail.trim().toLowerCase(), savedPassword);
            if (response.data && response.data.success) {
                toast.success(response.data.message || "User data deleted successfully.");
                setManualEmail('');
                loadRequests();
            } else {
                toast.error(response.data.message || "Failed to delete user.");
            }
        } catch (error) {
            console.error("Manual delete user error:", error);
            toast.error(
                error.response?.data?.message || 
                "Error executing deletion request."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleGrantAccessSubmit = async (e) => {
        e.preventDefault();
        if (!grantEmail) {
            toast.error("Please enter the user's email address.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await adminGrantAccess(
                grantEmail.trim().toLowerCase(), 
                grantExamType, 
                Number(grantDays), 
                savedPassword
            );
            if (response.data && response.data.success) {
                toast.success(response.data.message || `Successfully granted ${grantDays}-day access.`);
                setGrantEmail('');
            } else {
                toast.error(response.data.message || "Failed to grant access.");
            }
        } catch (error) {
            console.error("Grant access error:", error);
            toast.error(
                error.response?.data?.message || 
                "Error granting access. Please verify user exists."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchSubmit = async (e, customPassword = savedPassword) => {
        if (e) e.preventDefault();
        setIsSearching(true);
        try {
            const params = {
                search: searchQuery,
                testType: searchTestType || undefined,
                section: searchSection || undefined,
                dateRange: searchDateRange,
                startDate: searchDateRange === 'custom' ? searchStartDate : undefined,
                endDate: searchDateRange === 'custom' ? searchEndDate : undefined
            };
            const response = await searchUserAttempts(params, customPassword);
            if (response.data && response.data.success) {
                setAttempts(response.data.attempts || []);
            } else {
                toast.error("Failed to search attempts.");
            }
        } catch (error) {
            console.error("Search attempts error:", error);
            toast.error(
                error.response?.data?.message || 
                "Error searching mock test attempts."
            );
        } finally {
            setIsSearching(false);
        }
    };



    const handleLogout = () => {
        setIsAuthenticated(false);
        setPassword('');
        setSavedPassword('');
        setRequests([]);
        toast.info("Logged out from admin panel.");
    };

    // Render Login Screen
    if (!isAuthenticated) {
        return (
            <div className="admin-page">
                <Particle option={option2} />
                <div className="admin-login-container">
                    <h1 className="admin-heading">Admin Portal</h1>
                    <p className="admin-subheading">Enter administrative password to manage deletion requests</p>
                    <form onSubmit={handleLoginSubmit}>
                        <div className="input-group">
                            <input 
                                type="password" 
                                placeholder="Admin Password" 
                                value={password} 
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                                className="admin-password-input"
                            />
                        </div>
                        <button 
                            type="submit" 
                            className="admin-login-button" 
                            disabled={isLoading}
                        >
                            {isLoading ? "Authenticating..." : "Access Dashboard"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Render Dashboard
    return (
        <div className="admin-page">
            <Particle option={option2} />
            <div className="admin-dashboard-container">
                <div className="admin-dashboard-header">
                    <h1 className="admin-heading">Deletion Manager</h1>
                    <button onClick={handleLogout} className="admin-logout-button">
                        Logout Admin
                    </button>
                </div>

                <div className="admin-section">
                    <h2 className="admin-section-title">Pending Account Deletion Requests</h2>
                    {requests.length === 0 ? (
                        <div className="no-requests-msg">
                            No pending account deletion requests.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Details/Message</th>
                                        <th>Requested Date</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((req) => (
                                        <tr key={req._id}>
                                            <td>{req.name || <em className="text-muted">Not Provided</em>}</td>
                                            <td className="highlight-email">{req.email}</td>
                                            <td className="msg-cell">{req.message || <em className="text-muted">None</em>}</td>
                                            <td>{new Date(req.requestedAt).toLocaleString()}</td>
                                            <td>
                                                <button 
                                                    onClick={() => handleDeleteClick(req.email)}
                                                    className="btn-delete-action"
                                                    disabled={isLoading}
                                                >
                                                    Delete User & Data
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="admin-section admin-manual-section">
                    <h2 className="admin-section-title">Manual Account Deletion Override</h2>
                    <p className="admin-helper-text">
                        Delete any user and their associated database records immediately without a pre-existing request.
                    </p>
                    <form onSubmit={handleManualDeleteSubmit} className="admin-manual-form">
                        <input 
                            type="email" 
                            placeholder="user@example.com" 
                            value={manualEmail} 
                            onChange={(e) => setManualEmail(e.target.value)}
                            disabled={isLoading}
                            className="admin-manual-input"
                        />
                        <button 
                            type="submit" 
                            className="btn-delete-override"
                            disabled={isLoading}
                        >
                            Force Delete Account
                        </button>
                    </form>
                </div>

                <div className="admin-section admin-grant-section">
                    <h2 className="admin-section-title">Grant Exam Test Access</h2>
                    <p className="admin-helper-text">
                        Manually provide full 50-test access to either CELPIP or PTE Core practice systems for a specific duration.
                    </p>
                    <form onSubmit={handleGrantAccessSubmit} className="admin-grant-form">
                        <input 
                            type="email" 
                            placeholder="user@example.com" 
                            value={grantEmail} 
                            onChange={(e) => setGrantEmail(e.target.value)}
                            disabled={isLoading}
                            className="admin-grant-input"
                            required
                        />
                        <select 
                            value={grantExamType} 
                            onChange={(e) => setGrantExamType(e.target.value)}
                            disabled={isLoading}
                            className="admin-grant-select"
                        >
                            <option value="celpip">CELPIP</option>
                            <option value="pte">PTE Core</option>
                        </select>
                        <select 
                            value={grantDays} 
                            onChange={(e) => setGrantDays(Number(e.target.value))}
                            disabled={isLoading}
                            className="admin-grant-select"
                        >
                            <option value={30}>30 Days</option>
                            <option value={60}>60 Days</option>
                            <option value={90}>90 Days</option>
                        </select>
                        <button 
                            type="submit" 
                            className="btn-grant-action"
                            disabled={isLoading}
                        >
                            Grant Test Access
                        </button>
                    </form>
                </div>

                <AdminUploadSection />

                <div className="admin-section admin-search-attempts-section">
                    <h2 className="admin-section-title">Search User Mock Test Attempts</h2>
                    <p className="admin-helper-text">
                        Filter and view detailed question-by-question scoring and feedback for student attempts.
                    </p>
                    <form onSubmit={(e) => handleSearchSubmit(e)} className="admin-search-filter-form">
                        <div className="admin-search-form-row">
                            <input 
                                type="text" 
                                placeholder="Search by name or email..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)}
                                disabled={isSearching || isLoading}
                                className="admin-search-input"
                            />
                            
                            <select 
                                value={searchTestType} 
                                onChange={(e) => setSearchTestType(e.target.value)}
                                disabled={isSearching || isLoading}
                                className="admin-search-select"
                            >
                                <option value="">All Test Types</option>
                                <option value="celpip">CELPIP</option>
                                <option value="pte">PTE Core</option>
                            </select>

                            <select 
                                value={searchSection} 
                                onChange={(e) => setSearchSection(e.target.value)}
                                disabled={isSearching || isLoading}
                                className="admin-search-select"
                            >
                                <option value="">All Sections</option>
                                <option value="listening">Listening</option>
                                <option value="reading">Reading</option>
                                <option value="speaking">Speaking</option>
                                <option value="writing">Writing</option>
                            </select>

                            <select 
                                value={searchDateRange} 
                                onChange={(e) => setSearchDateRange(e.target.value)}
                                disabled={isSearching || isLoading}
                                className="admin-search-select"
                            >
                                <option value="all_time">All Time</option>
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="this_week">Past 7 Days</option>
                                <option value="custom">Custom Date Range</option>
                            </select>
                        </div>

                        {searchDateRange === 'custom' && (
                            <div className="admin-search-form-row date-inputs-row">
                                <div className="date-input-group">
                                    <label>Start Date</label>
                                    <input 
                                        type="date" 
                                        value={searchStartDate} 
                                        onChange={(e) => setSearchStartDate(e.target.value)}
                                        disabled={isSearching || isLoading}
                                        className="admin-search-date-input"
                                    />
                                </div>
                                <div className="date-input-group">
                                    <label>End Date</label>
                                    <input 
                                        type="date" 
                                        value={searchEndDate} 
                                        onChange={(e) => setSearchEndDate(e.target.value)}
                                        disabled={isSearching || isLoading}
                                        className="admin-search-date-input"
                                    />
                                </div>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="btn-search-action"
                            disabled={isSearching || isLoading}
                        >
                            {isSearching ? "Searching..." : "Search Attempts"}
                        </button>
                    </form>

                    {attempts.length === 0 ? (
                        <div className="no-requests-msg">
                            No student test attempts found matching the filters.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Student Name</th>
                                        <th>Student Email</th>
                                        <th>Test Type</th>
                                        <th>Section</th>
                                        <th>Test #</th>
                                        <th>Score</th>
                                        <th>Attempt Date</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attempts.map((att) => (
                                        <tr key={att._id}>
                                            <td>{att.studentName}</td>
                                            <td className="highlight-email">{att.studentEmail || <em className="text-muted">N/A</em>}</td>
                                            <td className="test-type-cell"><span className={`badge-type ${att.testType}`}>{att.testType.toUpperCase()}</span></td>
                                            <td className="section-cell"><span className={`badge-section ${att.section}`}>{att.section.toUpperCase()}</span></td>
                                            <td>{att.testNumber || 'N/A'}</td>
                                            <td className="score-cell">{att.score ?? 'N/A'}</td>
                                            <td>{new Date(att.attemptDate).toLocaleString()}</td>
                                            <td>
                                                <button 
                                                    onClick={() => setSelectedAttempt(att)}
                                                    className="btn-view-attempt-action"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {selectedAttempt && (
                <AttemptDetailsModal 
                    attempt={selectedAttempt} 
                    onClose={() => setSelectedAttempt(null)} 
                />
            )}
        </div>
    );
}

// Subcomponent: Attempt Detailed View Modal
const AttemptDetailsModal = ({ attempt, onClose }) => {
    if (!attempt) return null;

    const renderScoreBar = (label, score, maxScore = 12) => {
        const percentage = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
        return (
            <div className="admin-detail-score-bar-group">
                <div className="admin-detail-score-bar-labels">
                    <span>{label}</span>
                    <span>{score} / {maxScore}</span>
                </div>
                <div className="admin-detail-score-bar-bg">
                    <div className="admin-detail-score-bar-fill" style={{ width: `${percentage}%` }}></div>
                </div>
            </div>
        );
    };

    const renderCelpipSpeakingOrWriting = () => {
        const data = attempt.attemptData;
        const isSpeaking = attempt.section === 'speaking';
        return (
            <div className="admin-modal-content-section">
                <h3 className="admin-modal-content-title">{isSpeaking ? "Speaking Attempt" : "Writing Attempt"} Details</h3>
                <p className="admin-modal-meta-item"><strong>Test Number:</strong> {attempt.testNumber || 'N/A'}</p>
                
                {data.questionId && (
                    <div className="admin-detail-question-box">
                        <h4>Question Prompt:</h4>
                        <p className="admin-detail-text font-semibold">{data.questionId.questionTitle || 'Question'}</p>
                        <p className="admin-detail-text">{data.questionId.questionText}</p>
                    </div>
                )}

                <div className="admin-detail-response-box">
                    <h4>User Response:</h4>
                    <p className="admin-detail-text italic whitespace-pre-wrap">{isSpeaking ? data.transcription : data.typedAnswer}</p>
                </div>

                <div className="admin-detail-scores-box">
                    <h4>Scores & Feedback:</h4>
                    {isSpeaking ? (
                        <>
                            {renderScoreBar("Task Fulfillment", data.scores?.taskFulfillmentScore)}
                            {renderScoreBar("Coherence", data.scores?.coherenceScore)}
                            {renderScoreBar("Vocabulary", data.scores?.vocabularyScore)}
                            {renderScoreBar("Grammar", data.scores?.grammarScore)}
                            {renderScoreBar("Overall Score", data.scores?.overallScore)}

                            <div className="admin-detail-justifications">
                                <h5>AI Justifications:</h5>
                                <p><strong>Task Fulfillment:</strong> {data.justifications?.taskFulfillmentJustification || 'None'}</p>
                                <p><strong>Coherence:</strong> {data.justifications?.coherenceJustification || 'None'}</p>
                                <p><strong>Vocabulary:</strong> {data.justifications?.vocabularyJustification || 'None'}</p>
                                <p><strong>Grammar:</strong> {data.justifications?.grammarJustification || 'None'}</p>
                                <p><strong>Overall:</strong> {data.justifications?.overallJustification || 'None'}</p>
                            </div>
                        </>
                    ) : (
                        <>
                            {renderScoreBar("Task Fulfillment", data.scores?.taskFulfillmentScore)}
                            {renderScoreBar("Content & Coherence", data.scores?.contentScore)}
                            {renderScoreBar("Vocabulary", data.scores?.vocabularyScore)}
                            {renderScoreBar("Readability & Grammar", data.scores?.readabilityScore)}
                            {renderScoreBar("Overall Score", data.scores?.overallScore)}

                            <div className="admin-detail-justifications">
                                <h5>AI Justifications:</h5>
                                <p><strong>Task Fulfillment:</strong> {data.justifications?.taskFulfillmentJustification || 'None'}</p>
                                <p><strong>Content:</strong> {data.justifications?.contentJustification || 'None'}</p>
                                <p><strong>Vocabulary:</strong> {data.justifications?.vocabularyJustification || 'None'}</p>
                                <p><strong>Readability:</strong> {data.justifications?.readabilityJustification || 'None'}</p>
                                <p><strong>Overall:</strong> {data.justifications?.overallJustification || 'None'}</p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderCelpipReadingOrListening = () => {
        const data = attempt.attemptData;
        return (
            <div className="admin-modal-content-section">
                <h3 className="admin-modal-content-title">{attempt.section === 'listening' ? "Listening Attempt" : "Reading Attempt"} Details</h3>
                <p className="admin-modal-meta-item"><strong>Test Number:</strong> {attempt.testNumber || 'N/A'}</p>
                <p className="admin-modal-meta-item"><strong>Score Achieved:</strong> {data.attemptScore} / {data.answers?.length || 0} (CLB: {data.clbScore || 'N/A'})</p>

                <h4 className="mt-4 mb-2">Detailed Questions & Answers:</h4>
                <div className="admin-detail-answers-list">
                    {data.answers?.map((ans, idx) => (
                        <div key={ans._id || idx} className={`admin-detail-ans-item ${ans.isCorrect ? 'correct' : 'incorrect'}`}>
                            <p className="font-semibold text-white">Q{idx + 1}: {ans.questionId?.questionText || 'Question Text'}</p>
                            <p className="mt-1"><strong>Selected Answer:</strong> <span className={ans.isCorrect ? 'text-green' : 'text-red'}>{ans.selectedAnswer || '<No Answer>'}</span></p>
                            <p><strong>Correct Answer:</strong> <span className="text-green-light">{ans.correctAnswer || 'N/A'}</span></p>
                            <span className={`status-badge ${ans.isCorrect ? 'correct' : 'incorrect'}`}>
                                {ans.isCorrect ? "✓ Correct" : "✗ Incorrect"}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderPteSpeaking = () => {
        const data = attempt.attemptData;
        return (
            <div className="admin-modal-content-section">
                <h3 className="admin-modal-content-title">PTE Core Speaking Attempt</h3>
                <p className="admin-modal-meta-item"><strong>Test Number:</strong> {attempt.testNumber || 'N/A'} (Task: {data.taskTitle || data.component || 'N/A'})</p>
                {data.questionTitle && <p className="admin-modal-meta-item"><strong>Question:</strong> {data.questionTitle}</p>}
                {data.questionContent && (
                    <div className="admin-detail-question-box">
                        <h4>Prompt:</h4>
                        <p className="admin-detail-text">{data.questionContent}</p>
                    </div>
                )}

                <div className="admin-detail-scores-box">
                    <h4>Scores:</h4>
                    {renderScoreBar("Content", data.score?.content, 5)}
                    {renderScoreBar("Pronunciation", data.score?.pronunciation, 5)}
                    {renderScoreBar("Oral Fluency", data.score?.fluency, 5)}
                    {renderScoreBar("Overall Score", data.score?.overall, 90)}
                </div>

                <div className="admin-detail-response-box">
                    <h4>Audio Transcription:</h4>
                    <p className="admin-detail-text italic whitespace-pre-wrap">{data.score?.transcription || 'N/A'}</p>
                </div>

                {data.score?.feedback && (
                    <div className="admin-detail-feedback-box mt-4">
                        <h4>AI Feedback:</h4>
                        <p className="admin-detail-text whitespace-pre-wrap">{data.score.feedback}</p>
                    </div>
                )}

                {data.score?.detailedAnalysis && data.score.detailedAnalysis.length > 0 && (
                    <div className="admin-detail-analysis-box mt-4">
                        <h4>Pronunciation Analysis:</h4>
                        <div className="admin-detail-analysis-words">
                            {data.score.detailedAnalysis.map((w, idx) => (
                                <span key={idx} className={`word-analysis ${w.status || ''}`}>
                                    {w.word}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPteWritingOrListening = () => {
        const data = attempt.attemptData;
        const isListening = attempt.section === 'listening';
        return (
            <div className="admin-modal-content-section">
                <h3 className="admin-modal-content-title">{isListening ? "PTE Summarize Spoken Text (Listening)" : "PTE Write Email (Writing)"}</h3>
                <p className="admin-modal-meta-item"><strong>Task Component:</strong> {data.task?.component || 'N/A'}</p>
                <p className="admin-modal-meta-item"><strong>Word Count:</strong> {data.wordCount || 0} words</p>

                {data.task?.Q1 && (
                    <div className="admin-detail-question-box">
                        <h4>Task Prompt/Transcript:</h4>
                        <p className="admin-detail-text whitespace-pre-wrap">{data.task.Q1}</p>
                    </div>
                )}

                <div className="admin-detail-response-box">
                    <h4>User response text:</h4>
                    <p className="admin-detail-text whitespace-pre-wrap italic">{data.responseText || 'N/A'}</p>
                </div>

                <div className="admin-detail-scores-box">
                    <h4>Scoring Breakdown:</h4>
                    {data.autoScore?.content && renderScoreBar("Content", data.autoScore.content.score, data.autoScore.content.maxScore)}
                    {data.autoScore?.form && renderScoreBar("Form", data.autoScore.form.score, data.autoScore.form.maxScore)}
                    {data.autoScore?.grammar && renderScoreBar("Grammar", data.autoScore.grammar.score, data.autoScore.grammar.maxScore)}
                    {data.autoScore?.vocabulary && renderScoreBar("Vocabulary", data.autoScore.vocabulary.score, data.autoScore.vocabulary.maxScore)}
                    {data.autoScore?.spelling && renderScoreBar("Spelling", data.autoScore.spelling.score, data.autoScore.spelling.maxScore)}
                    {data.autoScore?.emailConventions && renderScoreBar("Email Conventions", data.autoScore.emailConventions.score, data.autoScore.emailConventions.maxScore)}
                    {data.autoScore?.organization && renderScoreBar("Organization", data.autoScore.organization.score, data.autoScore.organization.maxScore)}
                    {data.autoScore?.overall && renderScoreBar("Overall Score", data.autoScore.overall.score, data.autoScore.overall.maxScore)}
                </div>

                <div className="admin-detail-justifications">
                    <h4>AI Feedback Comments:</h4>
                    {data.autoScore?.content?.comments && <p><strong>Content:</strong> {data.autoScore.content.comments}</p>}
                    {data.autoScore?.form?.comments && <p><strong>Form:</strong> {data.autoScore.form.comments}</p>}
                    {data.autoScore?.grammar?.comments && <p><strong>Grammar:</strong> {data.autoScore.grammar.comments}</p>}
                    {data.autoScore?.vocabulary?.comments && <p><strong>Vocabulary:</strong> {data.autoScore.vocabulary.comments}</p>}
                    {data.autoScore?.spelling?.comments && <p><strong>Spelling:</strong> {data.autoScore.spelling.comments}</p>}
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (attempt.testType === 'celpip') {
            if (attempt.section === 'speaking' || attempt.section === 'writing') {
                return renderCelpipSpeakingOrWriting();
            } else {
                return renderCelpipReadingOrListening();
            }
        } else {
            if (attempt.section === 'speaking') {
                return renderPteSpeaking();
            } else {
                return renderPteWritingOrListening();
            }
        }
    };

    return (
        <div className="admin-modal-backdrop" onClick={onClose}>
            <div className="admin-modal-container" onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header">
                    <h2>Mock Attempt Viewer</h2>
                    <button className="admin-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="admin-modal-body">
                    <div className="admin-modal-info-bar">
                        <span><strong>Student:</strong> {attempt.studentName} ({attempt.studentEmail || 'N/A'})</span>
                        <span><strong>Date:</strong> {new Date(attempt.attemptDate).toLocaleString()}</span>
                        <span><strong>Type:</strong> {attempt.testType.toUpperCase()} - {attempt.section.toUpperCase()}</span>
                    </div>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

// Subcomponent: Admin Media & Bulk File Uploader
const AdminUploadSection = () => {
    const [uploadMode, setUploadMode] = useState('single'); // 'single' | 'bulk'
    const [mediaType, setMediaType] = useState('image'); // 'image' | 'audio'

    // Single File State
    const [singleFile, setSingleFile] = useState(null);
    const [singleUploading, setSingleUploading] = useState(false);
    const [singleSuccessUrl, setSingleSuccessUrl] = useState('');
    const [copiedUrl, setCopiedUrl] = useState(false);

    // Bulk File State
    const [bulkFiles, setBulkFiles] = useState([]);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ total: 0, completed: 0, success: 0, failed: 0 });

    // Single file upload handler
    const handleSingleUpload = async () => {
        if (!singleFile) {
            toast.error("Please select a file first.");
            return;
        }

        setSingleUploading(true);
        setSingleSuccessUrl('');
        setCopiedUrl(false);

        try {
            const formData = new FormData();
            const isImage = mediaType === 'image';
            formData.append(isImage ? "file" : "audio", singleFile);

            const uploadEndpoint = isImage 
                ? 'https://engdemyeducation.com/uploadGrid/upload' 
                : '/upload';

            const response = await fetch(uploadEndpoint, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const data = await response.json();
            const returnedUrl = data.fileUrl || data.url;

            if (returnedUrl) {
                const fullUrl = returnedUrl.startsWith('http') 
                    ? returnedUrl 
                    : `https://engdemyeducation.com${returnedUrl}`;
                setSingleSuccessUrl(fullUrl);
                toast.success("File uploaded successfully!");
            } else {
                toast.success("Upload completed!");
            }
        } catch (error) {
            console.error("Single upload error:", error);
            toast.error(error.message || "Failed to upload file. Please try again.");
        } finally {
            setSingleUploading(false);
        }
    };

    // Copy URL helper
    const handleCopyUrl = (url) => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        setCopiedUrl(true);
        toast.info("Copied file URL to clipboard!");
        setTimeout(() => setCopiedUrl(false), 3000);
    };

    // Bulk File Select handler
    const handleBulkFileChange = (e) => {
        const filesArray = Array.from(e.target.files || []);
        if (filesArray.length === 0) return;

        const preparedQueue = filesArray.map((file, idx) => ({
            id: `${file.name}-${Date.now()}-${idx}`,
            file,
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB',
            status: 'pending',
            url: '',
            error: ''
        }));

        setBulkFiles(prev => [...prev, ...preparedQueue]);
        setBulkProgress(prev => ({
            total: prev.total + preparedQueue.length,
            completed: prev.completed,
            success: prev.success,
            failed: prev.failed
        }));
    };

    // Start Bulk Upload
    const handleStartBulkUpload = async () => {
        const pendingItems = bulkFiles.filter(item => item.status === 'pending' || item.status === 'failed');
        if (pendingItems.length === 0) {
            toast.info("No pending files in bulk queue to upload.");
            return;
        }

        setBulkUploading(true);
        let successCount = bulkProgress.success;
        let failedCount = bulkProgress.failed;
        let completedCount = bulkProgress.completed;

        const isImage = mediaType === 'image';
        const uploadEndpoint = isImage 
            ? 'https://engdemyeducation.com/uploadGrid/upload' 
            : '/upload';

        for (let i = 0; i < bulkFiles.length; i++) {
            const item = bulkFiles[i];
            if (item.status === 'success') continue;

            setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));

            try {
                const formData = new FormData();
                formData.append(isImage ? "file" : "audio", item.file);

                const response = await fetch(uploadEndpoint, {
                    method: 'POST',
                    body: formData,
                });

                if (response.ok) {
                    const data = await response.json();
                    const returnedUrl = data.fileUrl || data.url;
                    const fullUrl = returnedUrl ? (returnedUrl.startsWith('http') ? returnedUrl : `https://engdemyeducation.com${returnedUrl}`) : '';

                    successCount++;
                    completedCount++;
                    setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'success', url: fullUrl } : f));
                } else {
                    failedCount++;
                    completedCount++;
                    setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'failed', error: `${response.status} ${response.statusText}` } : f));
                }
            } catch (err) {
                failedCount++;
                completedCount++;
                setBulkFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'failed', error: err.message } : f));
            }

            setBulkProgress({
                total: bulkFiles.length,
                completed: completedCount,
                success: successCount,
                failed: failedCount
            });

            await new Promise(r => setTimeout(r, 300));
        }

        setBulkUploading(false);
        toast.success("Bulk upload process completed!");
    };

    // Export Bulk Upload Log
    const handleExportLog = () => {
        if (bulkFiles.length === 0) {
            toast.info("No files in log to export.");
            return;
        }

        const logLines = bulkFiles.map(f => {
            if (f.status === 'success') {
                return `SUCCESS: ${f.name} -> ${f.url}`;
            } else if (f.status === 'failed') {
                return `FAILURE: ${f.name} -> ${f.error || 'Unknown error'}`;
            } else {
                return `PENDING: ${f.name}`;
            }
        });

        const logContent = logLines.join('\n');
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `upload_log_${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
        toast.success("Upload log downloaded!");
    };

    // Clear queue
    const handleClearQueue = () => {
        setBulkFiles([]);
        setBulkProgress({ total: 0, completed: 0, success: 0, failed: 0 });
    };

    return (
        <div className="admin-section admin-upload-manager-section">
            <h2 className="admin-section-title">
                <i className="fas fa-cloud-upload-alt mr-2" style={{ color: 'var(--adm-purple)' }}></i>
                Admin Media & Bulk File Manager
            </h2>
            <p className="admin-helper-text">
                Upload single media files or run batch bulk uploads to storage servers.
            </p>

            {/* Mode Switcher Tabs */}
            <div className="admin-upload-tabs">
                <button 
                    className={`admin-tab-btn ${uploadMode === 'single' ? 'active' : ''}`}
                    onClick={() => setUploadMode('single')}
                >
                    <i className="fas fa-file-upload"></i> Single File Upload
                </button>
                <button 
                    className={`admin-tab-btn ${uploadMode === 'bulk' ? 'active' : ''}`}
                    onClick={() => setUploadMode('bulk')}
                >
                    <i className="fas fa-layer-group"></i> Bulk Batch Upload
                </button>
            </div>

            {/* Media Type Selection */}
            <div className="admin-media-type-selector">
                <label className="type-radio-label">
                    <input 
                        type="radio" 
                        name="mediaType" 
                        value="image" 
                        checked={mediaType === 'image'}
                        onChange={() => setMediaType('image')}
                    />
                    <span><i className="fas fa-image"></i> Images (GridFS / Storage)</span>
                </label>
                <label className="type-radio-label">
                    <input 
                        type="radio" 
                        name="mediaType" 
                        value="audio" 
                        checked={mediaType === 'audio'}
                        onChange={() => setMediaType('audio')}
                    />
                    <span><i className="fas fa-file-audio"></i> Audio Files</span>
                </label>
            </div>

            {/* Single File Upload View */}
            {uploadMode === 'single' && (
                <div className="admin-single-upload-container">
                    <div className="upload-dropzone">
                        <input 
                            type="file" 
                            accept={mediaType === 'image' ? 'image/*' : 'audio/*'} 
                            onChange={(e) => {
                                setSingleFile(e.target.files[0] || null);
                                setSingleSuccessUrl('');
                            }}
                            id="single-file-input"
                            className="hidden-file-input"
                        />
                        <label htmlFor="single-file-input" className="dropzone-label">
                            <i className={`fas ${mediaType === 'image' ? 'fa-image' : 'fa-music'} dropzone-icon`}></i>
                            <span className="dropzone-text">
                                {singleFile ? singleFile.name : `Click or drag a file to upload as ${mediaType}`}
                            </span>
                            <span className="dropzone-subtext">
                                {singleFile ? `${(singleFile.size / 1024).toFixed(1)} KB` : `Accepts ${mediaType === 'image' ? 'JPG, PNG, GIF, WebP' : 'MP3, WAV, OGG, AAC'}`}
                            </span>
                        </label>
                    </div>

                    <div className="single-upload-actions">
                        <button 
                            className="btn-upload-submit"
                            onClick={handleSingleUpload}
                            disabled={!singleFile || singleUploading}
                        >
                            {singleUploading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i> Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload mr-2"></i> Upload {mediaType === 'image' ? 'Image' : 'Audio'}
                                </>
                            )}
                        </button>
                    </div>

                    {singleSuccessUrl && (
                        <div className="upload-success-card">
                            <div className="success-badge"><i className="fas fa-check-circle"></i> Upload Successful</div>
                            <div className="success-url-box">
                                <input type="text" readOnly value={singleSuccessUrl} className="url-input" />
                                <button className="btn-copy-url" onClick={() => handleCopyUrl(singleSuccessUrl)}>
                                    <i className={copiedUrl ? "fas fa-check" : "fas fa-copy"}></i> {copiedUrl ? "Copied!" : "Copy URL"}
                                </button>
                            </div>

                            {/* Preview */}
                            <div className="file-preview-wrapper mt-3">
                                {mediaType === 'image' ? (
                                    <div className="image-preview-container">
                                        <img src={singleSuccessUrl} alt="Uploaded preview" className="uploaded-image-preview" />
                                    </div>
                                ) : (
                                    <div className="audio-preview-container">
                                        <audio controls src={singleSuccessUrl} className="uploaded-audio-preview"></audio>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Bulk File Upload View */}
            {uploadMode === 'bulk' && (
                <div className="admin-bulk-upload-container">
                    <div className="upload-dropzone">
                        <input 
                            type="file" 
                            multiple
                            accept={mediaType === 'image' ? 'image/*' : 'audio/*'} 
                            onChange={handleBulkFileChange}
                            id="bulk-file-input"
                            className="hidden-file-input"
                            disabled={bulkUploading}
                        />
                        <label htmlFor="bulk-file-input" className="dropzone-label">
                            <i className="fas fa-folder-open dropzone-icon"></i>
                            <span className="dropzone-text">
                                Click or drag multiple files for Bulk Upload
                            </span>
                            <span className="dropzone-subtext">
                                Selected mode: {mediaType.toUpperCase()} upload
                            </span>
                        </label>
                    </div>

                    {/* Progress Bar & Controls */}
                    {bulkFiles.length > 0 && (
                        <div className="bulk-controls-panel">
                            <div className="bulk-stats-grid">
                                <div className="stat-card">
                                    <span className="stat-label">Total Files</span>
                                    <span className="stat-value">{bulkFiles.length}</span>
                                </div>
                                <div className="stat-card">
                                    <span className="stat-label">Uploaded</span>
                                    <span className="stat-value text-blue">{bulkProgress.completed} / {bulkFiles.length}</span>
                                </div>
                                <div className="stat-card">
                                    <span className="stat-label">Success</span>
                                    <span className="stat-value text-green">{bulkProgress.success}</span>
                                </div>
                                <div className="stat-card">
                                    <span className="stat-label">Failed</span>
                                    <span className="stat-value text-red">{bulkProgress.failed}</span>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="bulk-progress-bar-bg">
                                <div 
                                    className="bulk-progress-bar-fill"
                                    style={{ width: `${bulkFiles.length > 0 ? (bulkProgress.completed / bulkFiles.length) * 100 : 0}%` }}
                                ></div>
                            </div>

                            <div className="bulk-action-buttons">
                                <button 
                                    className="btn-start-bulk"
                                    onClick={handleStartBulkUpload}
                                    disabled={bulkUploading || bulkFiles.every(f => f.status === 'success')}
                                >
                                    {bulkUploading ? (
                                        <><i className="fas fa-spinner fa-spin mr-2"></i> Uploading Batch...</>
                                    ) : (
                                        <><i className="fas fa-play mr-2"></i> Start Bulk Upload</>
                                    )}
                                </button>

                                <button 
                                    className="btn-export-log"
                                    onClick={handleExportLog}
                                    disabled={bulkUploading}
                                >
                                    <i className="fas fa-download mr-2"></i> Export Log (.txt)
                                </button>

                                <button 
                                    className="btn-clear-queue"
                                    onClick={handleClearQueue}
                                    disabled={bulkUploading}
                                >
                                    <i className="fas fa-trash-alt mr-2"></i> Clear Queue
                                </button>
                            </div>

                            {/* Queue File List */}
                            <div className="table-responsive mt-4">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Filename</th>
                                            <th>Size</th>
                                            <th>Status</th>
                                            <th>URL / Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkFiles.map((item, idx) => (
                                            <tr key={item.id}>
                                                <td>{idx + 1}</td>
                                                <td><strong>{item.name}</strong></td>
                                                <td>{item.size}</td>
                                                <td>
                                                    <span className={`status-badge bulk-${item.status}`}>
                                                        {item.status === 'pending' && '⏳ Pending'}
                                                        {item.status === 'uploading' && '🔄 Uploading...'}
                                                        {item.status === 'success' && '✓ Success'}
                                                        {item.status === 'failed' && '✗ Failed'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.status === 'success' && item.url ? (
                                                        <div className="bulk-url-cell">
                                                            <span className="truncate-url">{item.url}</span>
                                                            <button 
                                                                className="btn-tiny-copy"
                                                                onClick={() => handleCopyUrl(item.url)}
                                                                title="Copy URL"
                                                            >
                                                                <i className="fas fa-copy"></i>
                                                            </button>
                                                        </div>
                                                    ) : item.status === 'failed' ? (
                                                        <span className="text-red font-semibold">{item.error || 'Error'}</span>
                                                    ) : (
                                                        <em className="text-muted">Waiting...</em>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPage;

